import {
    JobAPIType,
    AuthUserType,
    InstanceAPIType
} from "../../../Actions/Types"
import { Reducer } from "redux"
import {
    APIJobModel,
    JobResponseModel,
    getJobID,
    FeedbackMessageListParams,
    APIJobFeedbackMessageModel,
    JobHistoryModel,
    JobHistoryVersionResponseModel,
    APIJobCountByStatusParams,
    JobIDReviewModel
} from "../../../Models/API/JobModel"
import { AuthAction } from "../../Auth"
import { APIJobListParams } from "../../../Models/API/JobModel"
import {
    setPage,
    RemoteData,
    setPagesToStale,
    setItem,
    getInitialData,
    markAllPagesOfAllEntriesAsStale
} from "../../../Models/Client/RemoteData"
import {
    FetchState,
    getValue,
    setStale
} from "../../../Models/Client/FetchState"
import { RouterAction, LOCATION_CHANGE } from "connected-react-router"
import { abstractRoutePaths } from "../../../Routes"
import { matchPath } from "react-router"
import { Result } from "../../../Models/Utility/Result"
import { AppInstanceAction } from "../AppInstance/AppInstanceReducer"
import { isAPIError } from "../../../Models/API/Error"
import { updateAt } from "../../../Helpers/Functions/reducer"
import {
    APIFeedbackMessage,
    APIFeedbackTag
} from "../../../Models/API/JobFeedbackModel"
import { APIJobsPerStatusModel } from "../../../Models/API/APIJobsPerStatusModel"
import { Map } from "immutable"

export type JobCountStateKey = "message" | "app" | "jobList"

interface JobCount {
    data: APIJobsPerStatusModel
    params: APIJobCountByStatusParams
}
export interface IJobState {
    table: {
        [appID: string]:
            | RemoteData<APIJobModel, APIJobListParams, string>
            | undefined
    }
    messageTable: {
        [appID: string]:
            | RemoteData<APIJobFeedbackMessageModel, APIJobListParams, string>
            | undefined
    }
    outputs: {
        [jobID: string]: FetchState<JobResponseModel>
    }
    lastUpdateTimestamps: {
        [appID: string]: number | undefined
    }
    detailPage: {
        isInDetailPage: boolean
        timeOfEntering?: Date
        shouldRefreshListOnExit?: boolean
    }

    jobIdToLabel: {
        [appID: string]: FetchState<string | null>
    }
    jobIdToReview: {
        [appID: string]: FetchState<JobIDReviewModel | "nothing_to_review">
    }

    jobCount: {
        [appID: string]: Map<JobCountStateKey, FetchState<JobCount>> | undefined
    }
    jobIdToConversation: {
        [jobID: string]:
            | RemoteData<APIFeedbackMessage, FeedbackMessageListParams, string>
            | undefined
    }
    feedbackTags: {
        [appID: string]: FetchState<APIFeedbackTag[]>
    }
    history: {
        [jobID: string]: FetchState<JobHistoryModel>
    }
    historyVersionResponse: {
        [jobID: string]: {
            [version: number]: FetchState<JobHistoryVersionResponseModel>
        }
    }
}

export type JobState = IJobState

interface JobSetListAction {
    type: JobAPIType.setList
    meta: {
        appID: string
        page: number
        total: number
        params: APIJobListParams
    }
    payload: APIJobModel[]
}

interface JobSetResponseAction {
    type: JobAPIType.setResponse
    meta: {
        jobID: string
    }
    payload: JobResponseModel
}

export interface JobSetOneAction {
    type: JobAPIType.setOne
    meta: {
        appID: string
        jobID: string
    }
    payload?: APIJobModel
}

export interface JobSetHistoryAction {
    type: JobAPIType.setJobHistory
    meta: {
        jobID: string
    }
    payload: JobHistoryModel
}

export interface JobSetHistoryVersionResponseAction {
    type: JobAPIType.setJobHistoryVersionResponse
    meta: {
        jobID: string
        jobVersion: number
    }
    payload: JobHistoryVersionResponseModel
}

interface JobWithMessageSetOneAction {
    type: JobAPIType.setOneJobWithMessage
    meta: {
        appID: string
        jobID: string
    }
    payload?: APIJobFeedbackMessageModel
}

interface JobSetResponseErrorAction {
    type: JobAPIType.setResponseError
    meta: {
        jobID: string
    }
    payload: string // error message
}

interface JobSetIdToLabelAction {
    type: JobAPIType.setJobIdToLabel
    meta: {
        appID: string
    }
    payload: string | null
}

interface JobSetIdToReviewAction {
    type: JobAPIType.setJobIdToReview
    meta: {
        appID: string
    }
    payload: JobIDReviewModel | "nothing_to_review"
}

interface JobSetJobCountAction {
    type: JobAPIType.setJobCount
    meta: {
        appID: string
        stateKey: JobCountStateKey
        params: APIJobCountByStatusParams
    }
    payload: APIJobsPerStatusModel
}

interface JobInvalidateJobCountAction {
    type: JobAPIType.invalidateJobCount
    meta: {
        appID: string
    }
}

interface JobSetConversationAction {
    type: JobAPIType.setJobConversation
    meta: {
        jobID: string
        params: FeedbackMessageListParams
        page: number
        total: number
    }
    payload: APIFeedbackMessage[]
}

interface JobAddMessageAction {
    type: JobAPIType.addJobMessage
    meta: {
        jobID: string
    }
}

interface FeedbackTagsAction {
    type: JobAPIType.setFeedbackTags
    meta: {
        appID: string
    }
    payload: APIFeedbackTag[]
}

const getMessageId = (model: APIFeedbackMessage) => model.id

interface JobSetMessageListAction {
    type: JobAPIType.setMessageList
    meta: {
        appID: string
        page: number
        total: number
        params: APIJobListParams
    }
    payload: APIJobFeedbackMessageModel[]
}

export type JobAction =
    | JobSetListAction
    | JobSetResponseAction
    | JobSetResponseErrorAction
    | JobSetOneAction
    | JobSetIdToLabelAction
    | JobSetIdToReviewAction
    | JobSetJobCountAction
    | JobInvalidateJobCountAction
    | JobSetConversationAction
    | JobAddMessageAction
    | FeedbackTagsAction
    | JobSetMessageListAction
    | JobWithMessageSetOneAction
    | JobSetHistoryAction
    | JobSetHistoryVersionResponseAction

const initialState: JobState = {
    table: {},
    messageTable: {},
    outputs: {},
    lastUpdateTimestamps: {},
    detailPage: { isInDetailPage: false },
    jobIdToLabel: {},
    jobIdToReview: {},
    jobCount: {},
    jobIdToConversation: {},
    feedbackTags: {},
    history: {},
    historyVersionResponse: {}
}

function applyUpdateTime(
    state: JobState,
    appID: string,
    time: number
): JobState {
    const prevTimestamp = state.lastUpdateTimestamps[appID]
    const isUpdated =
        prevTimestamp !== undefined &&
        time !== undefined &&
        prevTimestamp < time

    const stateWithNewTimestamp = {
        ...state,
        lastUpdateTimestamps: {
            ...state.lastUpdateTimestamps,
            [appID]: time
        }
    }

    if (!isUpdated) {
        return stateWithNewTimestamp
    }

    return {
        ...stateWithNewTimestamp,
        table: {
            ...state.table,
            [appID]: setPagesToStale(state.table[appID] ?? getInitialData(), 0)
        },
        jobIdToReview: {
            ...state.jobIdToReview,
            [appID]: setStale(state.jobIdToReview[appID])
        },
        messageTable: {
            ...state.messageTable,
            [appID]: setPagesToStale(
                state.messageTable[appID] ?? getInitialData(),
                0
            )
        },
        jobCount: {
            ...state.jobCount,
            [appID]: state.jobCount[appID]?.map(setStale)
        }
    }
}

export const JobReducer: Reducer<
    JobState,
    JobAction | AuthAction | RouterAction | AppInstanceAction
> = (state = initialState, action): JobState => {
    switch (action.type) {
        case LOCATION_CHANGE:
            const isEnteringDetailPage =
                matchPath(action.payload.location.pathname, {
                    path: abstractRoutePaths.jobsDetail
                }) !== null ||
                matchPath(action.payload.location.pathname, {
                    path: abstractRoutePaths.jobsMessageDetail
                }) !== null
            // trigger job list when navigation occurrs, except when job detail modal is opened/closed
            const wasInDetailPage = state.detailPage.isInDetailPage
            const shouldMarkListAsStale =
                !isEnteringDetailPage && !wasInDetailPage
            const shouldMarkMessagesListStale =
                !isEnteringDetailPage &&
                wasInDetailPage &&
                !!state.detailPage.shouldRefreshListOnExit
            return {
                ...state,
                table: shouldMarkListAsStale
                    ? markAllPagesOfAllEntriesAsStale(state.table)
                    : state.table,
                messageTable:
                    shouldMarkListAsStale || shouldMarkMessagesListStale
                        ? markAllPagesOfAllEntriesAsStale(state.messageTable)
                        : state.messageTable,
                detailPage: {
                    isInDetailPage: isEnteringDetailPage,
                    timeOfEntering: !isEnteringDetailPage
                        ? undefined
                        : isEnteringDetailPage && !wasInDetailPage
                        ? new Date()
                        : state.detailPage.timeOfEntering,
                    shouldRefreshListOnExit: !isEnteringDetailPage
                        ? undefined
                        : state.detailPage.shouldRefreshListOnExit
                },
                jobIdToLabel: {},
                jobIdToReview: Object.entries(state.jobIdToReview).reduce(
                    (passed, [appID, data]) => ({
                        ...passed,
                        [appID]: setStale(data)
                    }),
                    {}
                ),
                jobIdToConversation: !isEnteringDetailPage
                    ? markAllPagesOfAllEntriesAsStale(state.jobIdToConversation)
                    : state.jobIdToConversation
            }
        case JobAPIType.setList: {
            const appID = action.meta.appID

            return {
                ...state,
                table: updateAt(state.table, appID, table =>
                    setPage(
                        table ?? getInitialData(),
                        action.meta.params,
                        action.payload,
                        getJobID,
                        action.meta.page,
                        action.meta.total
                    )
                )
            }
        }
        case InstanceAPIType.progressBatchSubmit: {
            const appID = action.meta.appID

            return {
                ...state,
                table: updateAt(state.table, appID, table =>
                    setPage(
                        table ?? getInitialData(),
                        action.meta.params,
                        action.payload.jobs,
                        getJobID,
                        action.meta.page,
                        action.meta.filteredTotal
                    )
                ),
                jobCount:
                    action.meta.params === state.table[appID]?.list?.params
                        ? state.jobCount
                        : {
                              ...state.jobCount,
                              [appID]: state.jobCount[appID]?.map(setStale)
                          }
            }
        }
        case JobAPIType.setOne:
            return {
                ...state,
                table: updateAt(state.table, action.meta.appID, table =>
                    setItem(
                        table ?? getInitialData(),
                        action.meta.jobID,
                        action.payload,
                        getJobID
                    )
                )
            }
        case JobAPIType.setResponse:
            const prevValue = state.outputs[action.meta.jobID]
            const newValue = Result.ok(action.payload)
            if (JSON.stringify(prevValue) === JSON.stringify(newValue)) {
                return state
            }
            return {
                ...state,
                outputs: {
                    ...state.outputs,
                    [action.meta.jobID]: newValue
                }
            }
        case JobAPIType.setResponseError:
            return {
                ...state,
                outputs: {
                    ...state.outputs,
                    [action.meta.jobID]: Result.error(action.payload)
                }
            }
        case JobAPIType.setJobIdToLabel:
            return {
                ...state,
                jobIdToLabel: {
                    ...state.jobIdToLabel,
                    [action.meta.appID]: Result.ok(action.payload)
                }
            }
        case JobAPIType.setJobIdToReview:
            if (
                JSON.stringify(
                    getValue(state.jobIdToReview[action.meta.appID])
                ) === JSON.stringify(action.payload)
            ) {
                return state
            }

            return {
                ...state,
                jobIdToReview: {
                    ...state.jobIdToReview,
                    [action.meta.appID]: Result.ok(action.payload)
                }
            }
        case JobAPIType.setJobCount:
            const appID = action.meta.appID
            const newJobCount = {
                data: action.payload,
                params: action.meta.params
            }

            return {
                ...state,
                jobCount: {
                    ...state.jobCount,
                    [appID]: (
                        state.jobCount[appID] ??
                        Map<JobCountStateKey, FetchState<JobCount>>()
                    ).set(action.meta.stateKey, Result.ok(newJobCount))
                }
            }
        case JobAPIType.invalidateJobCount:
            return {
                ...state,
                jobCount: {
                    ...state.jobCount,
                    [action.meta.appID]:
                        state.jobCount[action.meta.appID]?.map(setStale)
                }
            }
        case JobAPIType.setJobConversation:
            return {
                ...state,
                jobIdToConversation: {
                    ...state.jobIdToConversation,
                    [action.meta.jobID]: setPage(
                        state.jobIdToConversation[action.meta.jobID] ??
                            getInitialData(),
                        action.meta.params,
                        action.payload,
                        getMessageId,
                        action.meta.page,
                        action.meta.total
                    )
                }
            }
        case JobAPIType.addJobMessage:
            const currentConversation =
                state.jobIdToConversation[action.meta.jobID] ?? getInitialData()
            return {
                ...state,
                jobIdToConversation: {
                    ...state.jobIdToConversation,
                    [action.meta.jobID]: setPagesToStale(currentConversation, 0)
                },
                detailPage: {
                    ...state.detailPage,
                    shouldRefreshListOnExit: true
                }
            }
        case JobAPIType.setFeedbackTags:
            return {
                ...state,
                feedbackTags: {
                    ...state.feedbackTags,
                    [action.meta.appID]: Result.ok(action.payload)
                }
            }
        case JobAPIType.setMessageList:
            return {
                ...state,
                messageTable: updateAt(
                    state.messageTable,
                    action.meta.appID,
                    messageTable =>
                        setPage(
                            messageTable ?? getInitialData(),
                            action.meta.params,
                            action.payload,
                            getJobID,
                            action.meta.page,
                            action.meta.total
                        )
                )
            }
        case JobAPIType.setOneJobWithMessage:
            return {
                ...state,
                messageTable: updateAt(
                    state.messageTable,
                    action.meta.appID,
                    messageTable =>
                        setItem(
                            messageTable ?? getInitialData(),
                            action.meta.jobID,
                            action.payload,
                            getJobID
                        )
                )
            }
        case InstanceAPIType.addInstance: {
            const nextTimestamp =
                action.payload.job_stats === null ||
                isAPIError(action.payload.job_stats) ||
                !("jobsUpdatedTime" in action.payload.job_stats)
                    ? undefined
                    : +new Date(action.payload.job_stats.jobsUpdatedTime)

            return nextTimestamp === undefined
                ? state
                : applyUpdateTime(state, action.payload.uuid, nextTimestamp)
        }
        case AuthUserType.signedOut:
            return initialState
        case JobAPIType.setJobHistory:
            return {
                ...state,
                history: {
                    ...state.history,
                    [action.meta.jobID]: Result.ok(action.payload)
                }
            }
        case JobAPIType.setJobHistoryVersionResponse:
            return {
                ...state,
                historyVersionResponse: {
                    ...state.historyVersionResponse,
                    [action.meta.jobID]: {
                        ...state.historyVersionResponse[action.meta.jobID],
                        [action.meta.jobVersion]: Result.ok(action.payload)
                    }
                }
            }
        default:
            return state
    }
}
